/**
 * Who the viewer is, in the workspace they are in — the two lines the figures
 * put in the sidebar block and the one line they put in the top-bar chip:
 *
 *   Meridian Ventures            Meridian Ventures — Billing Owner
 *   Aggregator · Billing Owner
 *
 * Every value is real: the workspace name comes from the membership, the role
 * from the caller's own membership row. Billing Owner is a ROLE NAME — what a
 * person may do, never what they have bought — and every aggregator role sees
 * the same rail.
 */
import type { Workspace } from '../../contexts/WorkspaceContext'
import type { AggregatorMembership, User } from '../../types'

const AGGREGATOR_ROLE_LABEL: Record<string, string> = {
  billing_owner: 'Billing Owner',
  admin: 'Admin',
  analyst: 'Analyst',
}

/** The caller's role in this workspace, in words, or undefined when we have none. */
export function workspaceRoleLabel(
  workspace: Workspace,
  aggregatorMemberships: AggregatorMembership[] | undefined,
): string | undefined {
  if (workspace.kind === 'organisation') return workspace.roleLabel
  if (workspace.kind === 'aggregator') {
    const m = (aggregatorMemberships ?? []).find((x) => x.aggregator_id === workspace.entityId)
    return m ? AGGREGATOR_ROLE_LABEL[m.role] ?? undefined : undefined
  }
  return undefined
}

/** The top-bar chip: "Meridian Ventures — Billing Owner", "Personal". */
export function workspaceChipLabel(
  workspace: Workspace,
  aggregatorMemberships: AggregatorMembership[] | undefined,
): string {
  const role = workspaceRoleLabel(workspace, aggregatorMemberships)
  return role ? `${workspace.name} — ${role}` : workspace.name
}

const KIND_WORD: Record<Workspace['kind'], string> = {
  personal: 'Personal',
  organisation: 'Organisation',
  aggregator: 'Aggregator',
  admin: 'Platform admin',
}

/** The sidebar block's two lines. */
/**
 * The viewer's own role in the Personal workspace, in words — "Inventor" — or
 * undefined.
 *
 * Undefined unless the ID token really carried `custom:role`. AuthContext
 * defaults an ABSENT claim to 'inventor', so reading `user.role` here would
 * print a role for every account whether or not one was ever recorded. The
 * figures draw the clause (S4, S4b, C2 read "Dr. Amara Okafor · Founder"), but
 * a made-up one is worse than none: it is a fact about a person that nothing
 * backs. Measured on dev 2026-09-06 — none of bob, roger, charlie or beta
 * carries the claim, so none of them shows the clause today.
 */
export function personalRoleLabel(user: Pick<User, 'role' | 'role_claimed'> | null | undefined): string | undefined {
  if (!user?.role_claimed) return undefined
  const role = (user.role ?? '').trim()
  if (!role) return undefined
  return role
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export function workspaceIdentity(
  workspace: Workspace,
  aggregatorMemberships: AggregatorMembership[] | undefined,
  viewerName?: string,
  /** The viewer's own role, from personalRoleLabel — omitted when unrecorded. */
  viewerRole?: string,
): { title: string; subtitle?: string } {
  if (workspace.kind === 'personal') {
    const name = viewerName || undefined
    // The figures' "Dr. Amara Okafor · Founder". Name alone when no role is
    // recorded; never a role alone, which would name nobody.
    return { title: 'Personal workspace', subtitle: name && viewerRole ? `${name} · ${viewerRole}` : name }
  }
  const role = workspaceRoleLabel(workspace, aggregatorMemberships)
  const word = KIND_WORD[workspace.kind]
  return { title: workspace.name, subtitle: role ? `${word} · ${role}` : word }
}
